// Dependencies
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Core & Lab
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
// Icons
import PhotoCamera from '@mui/icons-material/PhotoCamera';
// Components
// Actions
import { updatePicturesForm } from './redux/actions';
// Utils
import { PictureContext } from './utils/pictureContext';
// Types
import { AppState } from '../../../../Reducers/Reducers';
import { EMPTY_PICTURE_FORM, PictureForm } from './objects/picture';
import PictureModalUploadEdit from './pictureModalUploadEdit';

const PictureModalCreateTabs: FC = (): ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const pictures = useSelector((state: AppState) => state.picture.files);
    const selection = useSelector((state: AppState) => state.picture.selection);
    const current_locale = useSelector((state: AppState) => state.locale.current_locale);


    const create_alt = useSelector((state: AppState) => state.picture.create_alt);
    const create_credit = useSelector((state: AppState) => state.picture.create_credit);
    const create_description = useSelector((state: AppState) => state.picture.create_description);
    const create_name = useSelector((state: AppState) => state.picture.create_name);
    const create_type = useSelector((state: AppState) => state.picture.create_type);

    const [edit_picture_index, setEditPictureIndex] = useState<number | null>(null);
    const [default_empty_picture, setDefaultEmptyPicture] = useState<PictureForm>(EMPTY_PICTURE_FORM);

    const default_description = useMemo(() => create_description ?? '', [create_description]);
    const default_alt = useMemo(() => create_alt ?? '', [create_alt]);
    const default_credit = useMemo(() => create_credit ?? '', [create_credit]);

    const default_type = 'PICTURE';

    const default_name = useMemo(() => {
        let name = '';
        if (create_name) {
            name = create_name;
        } else {
            name = t('shared.picture');
        }
        return name;
    }, [location, current_locale, create_name]);

    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, name: default_name }));
    }, [default_name]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, type: default_type }));
    }, [default_type]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, description: default_description }));
    }, [default_description]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, alt: default_alt }));
    }, [default_alt]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, credit: default_credit }));
    }, [default_credit]);

    const onPictureChange = (event: { target: { files: any; }; }) => {
        const updated_files = [];
        for (let i = 0; i < event.target.files.length; i++) {
            updated_files.push(event.target.files[i]);
            if ((event.target.files[i].size / 1024) / 1024 > 2) {
                enqueueSnackbar(t('errors.file-size-2'), { variant: 'error' });
            }
        }
        dispatch(updatePicturesForm([...pictures, ...updated_files.map((file) => ({ ...default_empty_picture, file }))]));
    };

    const onEditPicture = (index: number) => () => {
        setEditPictureIndex(index);
    };
    const onDeletePicture = (index: number) => () => {
        dispatch(updatePicturesForm(pictures.filter((_, i) => i !== index)));
    };
    return (<>
        <PictureContext.Consumer>
            {({ singleFile }) => (
                <Grid container spacing={ 2 }>
                    {
                        pictures?.map((picture, file_index: number) => (
                            <Grid item key={file_index} xs={ 4 }>
                                <Card>
                                    <CardMedia component={'img'} alt={''} height={200} image={URL.createObjectURL(picture?.file)} />
                                    <CardContent>
                                        <Typography>{picture.name}</Typography>
                                        <Typography className={'ft-redText'}>{(picture.file?.size / 1024) / 1024 > 2 ? t('errors.file-size-2') : ''}</Typography>
                                    </CardContent>
                                    <CardActions>
                                        <Button onClick={onEditPicture(file_index)}>{t('shared.update-picture')}</Button>
                                        <Button onClick={onDeletePicture(file_index)}>{t('shared.delete-picture')}</Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))
                    }
                    <Grid item xs={ 12 }>
                        <input accept={ 'image/*' } className={ 'ft-user-picture-input' } id={ 'icon-button-file' } type={ 'file' } multiple={!singleFile} onChange={ onPictureChange }/>
                        {
                            (!singleFile || (pictures?.length === 0 && selection.length === 0)) &&
                            <label htmlFor={ 'icon-button-file' }>
                                <Button className={ 'ft-user-picture-button' } startIcon={ <PhotoCamera className={ 'ft-user-picture-icon' }/> } component={ 'span' }>
                                    {t(singleFile ? 'shared.select-picture' : 'shared.select-picture(s)')} {t('shared.max-picture-size')}
                                </Button>
                            </label>
                        }
                    </Grid>
                </Grid>
            )}
        </PictureContext.Consumer>
        <PictureModalUploadEdit picture_index={edit_picture_index} setEditPictureIndex={setEditPictureIndex} />
    </>
    );
};

export default PictureModalCreateTabs;
